import { formatterPrice } from 'src/utils/formatterPrice'
import { useCheckout } from 'src/sdk/checkout/useCheckout'
import DktLoader from 'src/components/DktLoader/DktLoader'
import { InView } from 'react-intersection-observer'
import { useCallback } from 'react'
import dynamicYieldEngagement from 'src/utils/dynamicYield/dynamicYieldEngagement'
import {
  makeViewListImpressionEvent,
  sendEvent,
} from 'src/utils/restructure/analytics'
import { buildProductListObject } from 'src/utils/restructure/analytics/amplitude/buildProductListObject'
import { dispatchAmplitudeEvent } from 'src/utils/amplitude'
import { getStandardObject } from 'src/utils/amplitude/getStandardObject'
import { useGlobalContext } from 'src/components/contexts/GlobalContext'
import { CrossSellPlusIcon } from 'src/components/Icons/CrossSellPlusIcon'
import { useMobile } from 'src/hooks/useMobile'

import useCrossSellV2 from './useCrossSellV2'
import CrossSellSkeleton from './CrossSellSkeletonV2'
import ProductCardV2 from './components/ProductCardV2'
import BundleSkuSelectors from './components/BundleSkuSelectors'

const PriceBlock = ({
  totalCashPrice,
  totalListPrice,
}: {
  totalCashPrice: number
  totalListPrice: number
}) => {
  return (
    <div className="flex flex-col justify-center">
      <p className="text-base text-center md:text-left font-regular text-black mb-1">
        Compre os 2 produtos por
      </p>
      <p className="flex items-center justify-center md:justify-start gap-2">
        <span className="text-xl leading-5 font-medium text-black text-[32px]">
          {formatterPrice(totalCashPrice)}
        </span>
        {totalListPrice > totalCashPrice && (
          <span className="text-xs py-[2px] px-1 bg-redDiscount text-white font-semibold line-through">
            {formatterPrice(totalListPrice)}
          </span>
        )}
      </p>
    </div>
  )
}

const title = 'Compre junto'

const CrossSellV2 = () => {
  const { pageData } = useGlobalContext()
  const { isLoading } = useCheckout()
  const standardObj = getStandardObject(pageData)
  const { isMobile } = useMobile()
  const {
    currentProduct,
    currentSku,
    suggestionProduct,
    eventData,
    totalCashPrice,
    totalListPrice,
    loading,
    handleAddProducts,
    currentProductSelectedSku,
    suggestionProductSelectedSku,
    setCurrentProductSelectedSku,
    setSuggestionProductSelectedSku,
  } = useCrossSellV2()

  const handleOnViewEvents = useCallback(() => {
    if (eventData.decisionId) {
      dynamicYieldEngagement([
        {
          type: 'IMP',
          decisionId: eventData.decisionId,
          variations: [Number(eventData.id)],
        },
      ])
    }

    if (!suggestionProduct) {
      return
    }

    const event = makeViewListImpressionEvent(
      [
        {
          product: currentProduct,
          productItem: currentProduct?.items[0],
        },
        {
          product: suggestionProduct,
          productItem: suggestionProduct?.items[0],
        },
      ],
      {
        listName: `vitrine - Compre Junto${
          suggestionProduct?.sport ? ` - ${suggestionProduct?.sport}` : ''
        }`,
        dimension29: 'dy',
      }
    )

    sendEvent(event)

    const productListObj = buildProductListObject({
      listType: pageData.currentPageType,
      listName: `Vitrine: ${title}`,
      productsCount: 2,
    })

    dispatchAmplitudeEvent({
      eventName: 'Product List Viewed',
      eventData: {
        ...standardObj,
        ...productListObj,
      },
    })
  }, [
    currentProduct,
    eventData.decisionId,
    eventData.id,
    pageData.currentPageType,
    standardObj,
    suggestionProduct,
  ])

  if (!currentProduct || !suggestionProduct || !currentSku?.isAvailable) {
    return null
  }

  if (loading) {
    return <CrossSellSkeleton />
  }

  return (
    <InView
      key={`${currentProduct.productId}-${currentSku.itemId}`}
      as="div"
      triggerOnce
      onChange={(inView) => {
        if (inView) {
          handleOnViewEvents()
        }
      }}
      className="max-w-[1280px] w-full mx-auto mt-14 md:mt-20 p-4"
    >
      <p className="text-[28px] font-medium text-restructure-primary hidden md:flex mb-[31px]">
        Aproveite e leve junto
      </p>
      <div className="md:hidden mb-6">
        <PriceBlock
          totalCashPrice={totalCashPrice}
          totalListPrice={totalListPrice}
        />
      </div>

      <div className="flex flex-col md:flex-row">
        <div className="flex flex-col md:flex-row items-center md:items-start gap-2 w-full md:w-[61%]">
          <div className="cross-sell-current-product w-full grow max-w-[364px]">
            <ProductCardV2
              product={currentProduct}
              currentProduct
              onSelect={setCurrentProductSelectedSku}
              selectedSku={currentProductSelectedSku}
            />
          </div>
          <div className="-my-4 w-full md:w-fit max-w-[364px] md:my-auto z-10 flex items-center mr-auto md:mr-0 mx-auto md:justify-center">
            <div className="flex items-center justify-center ml-[60px] md:ml-0 min-w-[38px] min-h-[38px] bg-white rounded-full">
              <CrossSellPlusIcon
                width={isMobile ? 14 : 28}
                height={isMobile ? 14 : 28}
                color="#001018"
              />
            </div>
          </div>
          <div className="cross-sell-suggestion-product w-full grow max-w-[364px]">
            <ProductCardV2
              product={suggestionProduct}
              selectedSku={suggestionProductSelectedSku}
              onSelect={setSuggestionProductSelectedSku}
            />
          </div>
        </div>

        <div className="text-restructure-primary md:ml-11 md:mt-6 flex flex-col items-center w-full md:max-w-[393px]">
          <div className="w-full hidden md:flex flex-col gap-8">
            <PriceBlock
              totalCashPrice={totalCashPrice}
              totalListPrice={totalListPrice}
            />

            <BundleSkuSelectors
              setSuggestionProductSelectedSku={setSuggestionProductSelectedSku}
              setCurrentProductSelectedSku={setCurrentProductSelectedSku}
              suggestionProduct={suggestionProduct}
              suggestionProductSelectedSku={suggestionProductSelectedSku}
              currentProduct={currentProduct}
              currentProductSelectedSku={currentProductSelectedSku}
            />
          </div>
          <button
            className="mt-8 text-base font-semibold text-white h-12 bg-restructure-background-neutral-12 w-full rounded-round flex items-center justify-center"
            type="button"
            aria-label="Adicionar ambos ao carrinho"
            onClick={handleAddProducts}
          >
            {isLoading ? (
              <DktLoader width={20} variant="white" />
            ) : (
              'Adicionar todos ao carrinho'
            )}
          </button>
        </div>
      </div>
    </InView>
  )
}

export default CrossSellV2
